import React from 'react'
import { col } from './styles'
import { ClientGlyph, EdgeGlyph, StudioGlyph, type ClientGrain } from './glyphs'

const TIER = {
  0: '#e84040',
  1: '#f5a623',
  2: '#34c95f',
} as const

interface Props {
  layers: { studios: boolean; edges: boolean; clients: boolean; flows: boolean }
  clientGrain: ClientGrain
  showRegionStack?: boolean
}

export function Legend({ layers, clientGrain, showRegionStack }: Props) {
  const glyphs: Array<{ key: string; label: string; el: React.ReactNode }> = []
  if (layers.studios) {
    glyphs.push({
      key: 'studios',
      label: 'Studios',
      el: <StudioGlyph cx={6} cy={6} r={5} fill={TIER[2]} fillOpacity={0.4} stroke={TIER[2]} />,
    })
  }
  if (layers.edges) {
    glyphs.push({
      key: 'edges',
      label: 'CDN edges',
      el: <EdgeGlyph cx={6} cy={6} r={8} fill={TIER[1]} fillOpacity={0.9} stroke={TIER[1]} />,
    })
  }
  if (layers.clients) {
    glyphs.push({
      key: 'clients',
      label: clientGrain === 'continent' ? 'Clients (continent)'
        : clientGrain === 'country' ? 'Clients (country)'
        : 'Clients (city)',
      el: (
        <ClientGlyph
          cx={6}
          cy={6}
          r={4}
          grain={clientGrain}
          fill={TIER[2]}
          fillOpacity={0.55}
          stroke={TIER[2]}
        />
      ),
    })
  }
  if (layers.flows) {
    glyphs.push({
      key: 'flows',
      label: 'Flows',
      el: <path d="M1 9 Q6 1 11 9" fill="none" stroke={TIER[0]} strokeWidth={1.4} />,
    })
  }
  if (showRegionStack) {
    glyphs.push({
      key: 'region-stack',
      label: 'Region stack (worst inward)',
      el: (
        <>
          <circle cx={6} cy={6} r={5.5} fill={TIER[2]} fillOpacity={0.2} stroke={TIER[2]} strokeWidth={0.8} />
          <circle cx={6} cy={6} r={3.5} fill={TIER[1]} fillOpacity={0.25} stroke={TIER[1]} strokeWidth={0.8} />
          <circle cx={6} cy={6} r={1.8} fill={TIER[0]} fillOpacity={0.4} stroke={TIER[0]} strokeWidth={0.8} />
        </>
      ),
    })
  }

  if (!glyphs.length) return null

  return (
    <div
      style={{
        position: 'absolute',
        left: 10,
        bottom: 10,
        zIndex: 5,
        padding: '8px 10px',
        borderRadius: 8,
        background: 'rgba(18,18,28,0.88)',
        border: `0.5px solid ${col.bd}`,
        fontSize: 10,
        color: col.t1,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        pointerEvents: 'none',
        userSelect: 'none',
      }}
    >
      {glyphs.map((g) => (
        <div key={g.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <svg width={12} height={12} viewBox="0 0 12 12" aria-hidden>
            {g.el}
          </svg>
          <span>{g.label}</span>
        </div>
      ))}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginTop: 2,
          paddingTop: 4,
          borderTop: `0.5px solid ${col.bd}`,
        }}
      >
        {([0, 1, 2] as const).map((t) => (
          <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: TIER[t] }} />
            {t === 0 ? 'crit' : t === 1 ? 'warn' : 'ok'}
          </span>
        ))}
      </div>
    </div>
  )
}
